import { ExternalLink } from "lucide-react";
import { useRef, type ReactNode } from "react";
import { Link, NavLink, Outlet, ScrollRestoration, useNavigation } from "react-router";
import { pickText } from "../../schema/core.ts";
import { useSession } from "../auth.ts";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { useCourseKit } from "../provider.tsx";
import { cn } from "../ui/cn.ts";
import { AccountMenu } from "./AccountMenu.tsx";
import { LanguageToggle } from "./LanguageToggle.tsx";
import { RouteFocus } from "./RouteFocus.tsx";
import { visibleLinks, type SiteNavLink } from "./nav.ts";

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

interface SiteLayoutProps {
  /** Shown inline in the header from md up, and in the account menu. */
  navLinks?: SiteNavLink[];
  /** Shown only in the account menu (signed-in visitors). */
  accountLinks?: SiteNavLink[];
}

const SiteHeader = ({ navLinks = [], accountLinks = [] }: SiteLayoutProps): ReactNode => {
  const { brand, locale } = useCourseKit();
  const copy = useSiteCopy();
  const { user } = useSession();
  const tagline = brand.tagline ? pickText(brand.tagline, locale).trim() : "";
  const headerLinks = visibleLinks(navLinks, user);
  const menuLinks = [...headerLinks, ...visibleLinks(accountLinks, user)];

  return (
    <header className="ck-dark sticky top-0 z-30 border-b border-header-foreground/15 print:hidden">
      <div className="mx-auto flex min-h-header max-w-6xl items-center gap-3 px-4 py-2 sm:px-6">
        <Link to="/" className="mr-auto flex min-h-11 min-w-0 items-center gap-3 rounded-lg">
          {brand.logoSrc && <img src={brand.logoSrc} alt="" className="size-10 shrink-0" />}
          <span className="min-w-0">
            <span className="block truncate font-display text-lg leading-tight font-semibold">
              {brand.name}
            </span>
            {tagline && (
              <span className="hidden truncate text-base text-header-foreground/80 sm:block">
                {tagline}
              </span>
            )}
          </span>
        </Link>
        {headerLinks.length > 0 && (
          <nav aria-label={copy.header.navigation} className="hidden md:block">
            <ul className="flex items-center gap-1">
              {headerLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === "/"}
                    className={({ isActive }) =>
                      cn(
                        "inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-base",
                        "font-semibold transition-colors hover:text-highlight",
                        isActive
                          ? "bg-header-foreground/10 text-highlight"
                          : "text-header-foreground/85",
                      )
                    }>
                    {link.icon && <link.icon aria-hidden="true" className="size-4" />}
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
        {brand.homeUrl && (
          <a
            href={brand.homeUrl}
            className={cn(
              "hidden min-h-11 items-center gap-1.5 rounded-lg px-2 py-2 text-base",
              "text-header-foreground/85 hover:text-highlight md:inline-flex",
            )}>
            {copy.header.mainSite}
            <ExternalLink aria-hidden="true" className="size-4" />
          </a>
        )}
        <LanguageToggle />
        <AccountMenu links={menuLinks} />
      </div>
    </header>
  );
};

const SiteFooter = (): ReactNode => {
  const { brand } = useCourseKit();
  const copy = useSiteCopy();
  return (
    <footer className="ck-dark border-t border-header-foreground/15 print:hidden">
      <div
        className={cn(
          "mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-base",
          "sm:flex-row sm:items-center sm:justify-between sm:px-6",
        )}>
        <p className="font-display text-lg font-semibold">
          © {new Date().getFullYear()} {brand.name}
        </p>
        {brand.homeUrl && (
          <a
            href={brand.homeUrl}
            className={cn(
              "inline-flex min-h-11 items-center rounded-lg text-header-foreground/85",
              "underline-offset-4 hover:text-highlight hover:underline",
            )}>
            {copy.footer.backTo(hostOf(brand.homeUrl))}
          </a>
        )}
      </div>
    </footer>
  );
};

/**
 * Dark header band, paper reading area (each page sets its own width), dark footer.
 * Render it inside a data router: it restores the scroll position on Back/Forward
 * (new pages start at the top), moves focus to each new page and shows a bar while
 * the next page's code or data loads.
 */
export const SiteLayout = ({ navLinks, accountLinks }: SiteLayoutProps): ReactNode => {
  const copy = useSiteCopy();
  const mainRef = useRef<HTMLElement>(null);
  const navigating = useNavigation().state !== "idle";
  return (
    <div className="flex min-h-dvh flex-col">
      <ScrollRestoration />
      <RouteFocus mainRef={mainRef} />
      {navigating && (
        <div
          aria-hidden="true"
          className="fixed inset-x-0 top-0 z-50 h-1 bg-highlight motion-safe:animate-pulse print:hidden"
        />
      )}
      {/* Focus <main> directly: a fragment navigation would add a history entry without a
          router key, which ScrollRestoration would mix up with the first page's position. */}
      <a
        href="#main"
        onClick={(event) => {
          event.preventDefault();
          mainRef.current?.focus();
        }}
        className={cn(
          "sr-only z-50 rounded-lg bg-card px-4 py-2 font-semibold text-card-foreground",
          "focus:not-sr-only focus:fixed focus:top-3 focus:left-3 print:hidden",
        )}>
        {copy.skipToContent}
      </a>
      <SiteHeader navLinks={navLinks} accountLinks={accountLinks} />
      <main
        ref={mainRef}
        id="main"
        tabIndex={-1}
        aria-busy={navigating}
        className="flex-1 focus:outline-none">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
};
